import React, { useEffect, useState, useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { newsData } from "../../data/news-data";

function useEqualizeMasonryLastRow(count) {
  const cardRefs = useRef([]);
  const [styles, setStyles] = useState({});

  if (cardRefs.current.length !== count) {
    cardRefs.current = Array.from(
      { length: count },
      (_, i) => cardRefs.current[i] || null
    );
  }

  const measure = () => {
    const rects = cardRefs.current
      .map((el, i) => ({ i, el, rect: el?.getBoundingClientRect() }))
      .filter((x) => !!x.el && !!x.rect);

    if (rects.length === 0) return;

    const maxBottom = Math.max(...rects.map((r) => r.rect.bottom));
    const epsilon = 2;

    const lastRow = rects.filter((r) => maxBottom - r.rect.bottom <= epsilon);
    if (lastRow.length <= 1) {
      setStyles({});
      return;
    }

    const maxH = Math.max(...lastRow.map((r) => r.rect.height));
    const next = {};
    lastRow.forEach((r) => {
      next[r.i] = { minHeight: `${Math.ceil(maxH)}px` };
    });

    setStyles(next);
  };

  useLayoutEffect(() => {
    const id = requestAnimationFrame(measure);
    const onResize = () => requestAnimationFrame(measure);
    window.addEventListener("resize", onResize);

    const ro = new ResizeObserver(() => measure());
    cardRefs.current.forEach((el) => el && ro.observe(el));

    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener("resize", onResize);
      ro.disconnect();
    };
  }, [count]);

  return { cardRefs, lastRowStyles: styles };
}
function SkeletonCard() {
  return (
    <div className="mb-4 break-inside-avoid">
      <div className="overflow-hidden rounded-md bg-sdbodywhite">
        <div className="w-full h-48 bg-gray-200 animate-pulse" />
        <div className="bg-gray-100 p-3">
          <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
          <div className="mt-2 h-3 w-full bg-gray-200 rounded animate-pulse" />
          <div className="mt-1 h-3 w-5/6 bg-gray-200 rounded animate-pulse" />
          <div className="mt-3 h-3 w-24 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
    </div>
  );
}

const HomeLatestNews = () => {
  const reduce = useReducedMotion();
  const viewportOnce = { once: true, amount: 0.25 };

  const [news, setNews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const data = newsData || [];
      setTimeout(() => {
        if (!mounted) return;
        setNews(data);
        setIsLoading(false);
      }, 300);
    })();
    return () => (mounted = false);
  }, []);

  const { cardRefs, lastRowStyles } = useEqualizeMasonryLastRow(news.length);
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={[
        "relative py-14 px-4 transition-[max-height] duration-500 ease-in-out",
        expanded
          ? "max-h-[200rem] overflow-visible"
          : "max-h-[65rem] overflow-hidden",
      ].join(" ")}
    >
      <div className="text-center mb-6">
        <motion.h2
          className="text-2xl md:text-3xl font-bold text-sddarkgray"
          initial={reduce ? undefined : { opacity: 0, y: 12 }}
          whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
          viewport={viewportOnce}
          transition={{ duration: 0.45, ease: "easeOut" }}
        >
          Latest News & Updates
        </motion.h2>
        <motion.p
          className="text-gray-600 mt-2 text-sm md:text-base"
          initial={reduce ? undefined : { opacity: 0, y: 8 }}
          whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
          viewport={viewportOnce}
          transition={{ duration: 0.35, ease: "easeOut" }}
        >
          Stay informed with the latest insights & developments.
        </motion.p>
      </div>

      <div className="max-w-6xl mx-auto md:px-3">
        <div className="columns-1 md:columns-3 gap-4">
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={`sk-${i}`} />
              ))
            : news.map((item, i) => (
                <Card
                  key={item.id ?? item.title ?? i}
                  item={item}
                  forwardedRef={(el) => (cardRefs.current[i] = el)}
                  style={lastRowStyles[i] || undefined}
                  index={i}
                  reduce={reduce}
                  viewportOnce={viewportOnce}
                />
              ))}
        </div>
      </div>

      {!expanded && (
        <>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-white to-transparent" />
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-sddarkgray text-white px-4 py-2 shadow-md"
          >
            Show more
          </button>
        </>
      )}

      {expanded && (
        <div className="mt-6 md:flex md:justify-center hidden">
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="rounded-full border px-4 py-2 text-sddarkgray hover:bg-gray-50"
          >
            Show less
          </button>
        </div>
      )}
    </div>
  );
};

const Card = ({ item, forwardedRef, style, index, reduce, viewportOnce }) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setImgLoaded(true));
      });
    }
  }, []);

  const handleLoad = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setImgLoaded(true));
    });
  };

  const handleError = () => setImgError(true);

  const cardAspect = item?.aspectRatio || "16 / 9";
  const showImage =
    Boolean(item?.image || item?.image400 || item?.image800) && !imgError;

  const src = item.image800 || item.image400 || item.image;
  const srcSet = [
    item.image400 && `${item.image400} 400w`,
    item.image800 && `${item.image800} 800w`,
  ]
    .filter(Boolean)
    .join(", ");
  const sizes = "(min-width: 768px) 33vw, 100vw"; // md:columns-3 => ~33% width on md+, 100% below

  const Inner = ({ children }) =>
    item?.id ? (
      <Link
        to={`/news/${item.id}`}
        aria-label={`Read more about ${item.title}`}
        className="mb-4 block break-inside-avoid"
      >
        {children}
      </Link>
    ) : item?.link ? (
      <a
        href={item.link}
        target="_blank"
        rel="noopener noreferrer"
        className="mb-4 block break-inside-avoid"
      >
        {children}
      </a>
    ) : (
      <div className="mb-4 block break-inside-avoid">{children}</div>
    );

  return (
    <Inner>
      <motion.div
        ref={forwardedRef}
        style={style}
        className="group overflow-hidden rounded-md bg-sdorange"
        initial={reduce ? undefined : { opacity: 0, y: 12, scale: 0.98 }}
        whileInView={reduce ? undefined : { opacity: 1, y: 0, scale: 1 }}
        viewport={viewportOnce}
        transition={{
          duration: 0.45,
          ease: "easeOut",
          delay: reduce ? 0 : Math.min(index * 0.04, 0.4),
        }}
        whileHover={
          reduce
            ? undefined
            : { y: -2, scale: 1.01, transition: { duration: 0.15 } }
        }
        whileTap={reduce ? undefined : { scale: 0.995 }}
      >
        <div className="relative w-full" style={{ aspectRatio: cardAspect }}>
          {showImage ? (
            <>
              <img
                ref={imgRef}
                src={src}
                srcSet={srcSet || undefined}
                sizes={srcSet ? sizes : undefined}
                alt={item.title ?? ""}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out [will-change:opacity] ${
                  imgLoaded ? "opacity-100" : "opacity-0"
                } block`}
                width={800}
                height={450}
                loading="lazy"
                decoding="async"
                onLoad={handleLoad}
                onError={handleError}
              />
              <div
                aria-hidden="true"
                className={`absolute inset-0 pointer-events-none select-none transition-opacity duration-300 ease-in ${
                  imgLoaded ? "opacity-0" : "opacity-100"
                } ${imgLoaded ? "" : "animate-pulse bg-gray-200"}`}
              />
              <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none bg-black/5"
              />
            </>
          ) : (
            <div className="absolute inset-0 bg-gray-200" />
          )}
        </div>

        <div
          className="bg-gray-100 group-hover:bg-sddarkgray text-black group-hover:text-white p-3 
          transition-colors duration-300 ease-in-out"
        >
          {item?.title && <div className="font-bold">{item.title}</div>}
          {item?.minidescription && (
            <div className="mt-2 text-sm md:text-base leading-relaxed">
              {item.minidescription}
            </div>
          )}
          {item?.date && (
            <div className="mt-2 text-sm opacity-70">{item.date}</div>
          )}
        </div>
      </motion.div>
    </Inner>
  );
};

export default HomeLatestNews;
